import { strFromU8, unzipSync } from "fflate";
import * as XLSX from "xlsx";
import type { TargetareRandImportLista } from "@/types/targetare";

export type TabelParsat = {
  antet: string[];
  randuri: string[][];
  foaie?: string;
  foi?: string[];
  mod: "tabel" | "extras";
};

export type TipFisier =
  | "csv"
  | "xlsx"
  | "pdf"
  | "html"
  | "text"
  | "docx"
  | "necunoscut";

export type MapareColoane = {
  cui: number | null;
  denumire: number | null;
  cod_inmatriculare: number | null;
};

const TIPURI_EXCEL = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

export function detecteazaTipFisier(
  nume: string,
  contentType?: string | null
): TipFisier {
  const ext = (nume.split(".").pop() ?? "").toLowerCase();
  if (["xlsx", "xls", "xlsm", "ods"].includes(ext)) return "xlsx";
  if (ext === "csv" || ext === "tsv") return "csv";
  if (ext === "pdf") return "pdf";
  if (ext === "html" || ext === "htm") return "html";
  if (ext === "txt") return "text";
  if (ext === "docx" || ext === "doc") return "docx";
  const ct = (contentType ?? "").toLowerCase();
  if (TIPURI_EXCEL.some((t) => ct.includes(t)) || ct.includes("spreadsheet"))
    return "xlsx";
  if (ct.includes("pdf")) return "pdf";
  if (ct.includes("html")) return "html";
  if (ct.includes("csv")) return "csv";
  if (ct.includes("text/plain")) return "text";
  if (ct.includes("wordprocessingml") || ct.includes("msword")) return "docx";
  return "necunoscut";
}

export function decodeazaBase64(b64: string): ArrayBuffer {
  const binar = atob(b64);
  const octeti = new Uint8Array(binar.length);
  for (let i = 0; i < binar.length; i++) octeti[i] = binar.charCodeAt(i);
  return octeti.buffer;
}

export function decodeazaText(buffer: ArrayBuffer): string {
  const octeti = new Uint8Array(buffer);
  const utf8 = new TextDecoder("utf-8", { fatal: false }).decode(octeti);
  if (!utf8.includes("�")) return utf8.replace(/^﻿/, "");
  return new TextDecoder("windows-1250").decode(octeti);
}

function detecteazaDelimitator(text: string): string {
  const linii = text.split(/\r?\n/).slice(0, 20);
  const candidati = [",", ";", "\t", "^", "|"];
  let ales = ",";
  let maxim = 0;
  for (const d of candidati) {
    const numar = linii.reduce((s, l) => s + (l.split(d).length - 1), 0);
    if (numar > maxim) {
      maxim = numar;
      ales = d;
    }
  }
  return ales;
}

export function parseazaCsvText(text: string): TabelParsat {
  const delimitator = detecteazaDelimitator(text);
  const randuri: string[][] = [];
  let rand: string[] = [];
  let camp = "";
  let inGhilimele = false;
  const curat = text.replace(/^﻿/, "");
  for (let i = 0; i < curat.length; i++) {
    const c = curat[i];
    if (inGhilimele) {
      if (c === '"') {
        if (curat[i + 1] === '"') {
          camp += '"';
          i++;
        } else {
          inGhilimele = false;
        }
      } else {
        camp += c;
      }
    } else if (c === '"') {
      inGhilimele = true;
    } else if (c === delimitator) {
      rand.push(camp);
      camp = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && curat[i + 1] === "\n") i++;
      rand.push(camp);
      if (rand.some((x) => x.trim() !== "")) randuri.push(rand);
      rand = [];
      camp = "";
    } else {
      camp += c;
    }
  }
  rand.push(camp);
  if (rand.some((x) => x.trim() !== "")) randuri.push(rand);
  const antet = (randuri.shift() ?? []).map(
    (h, i) => h.trim() || `col${i + 1}`
  );
  return {
    antet,
    randuri: randuri.map((r) => r.map((v) => v.trim())),
    mod: "tabel",
  };
}

function celulaText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : String(v);
  return String(v).trim();
}

function ridicaAntetul(randuri: string[][]): {
  antet: string[];
  corp: string[][];
} {
  let idx = 0;
  let scorMax = -1;
  const limita = Math.min(randuri.length, 15);
  for (let i = 0; i < limita; i++) {
    const r = randuri[i];
    const nenule = r.filter((v) => v.trim() !== "").length;
    const texte = r.filter(
      (v) => v.trim() !== "" && !/^\d+([.,]\d+)?$/.test(v)
    ).length;
    const scor = nenule + texte;
    if (scor > scorMax) {
      scorMax = scor;
      idx = i;
    }
  }
  const antet = randuri[idx].map((h, i) => h.trim() || `col${i + 1}`);
  return { antet, corp: randuri.slice(idx + 1) };
}

function decodeazaEntitatiXml(s: string): string {
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_m, n) => String.fromCharCode(Number(n)))
    .replace(/&amp;/g, "&");
}

function coloanaDinReferinta(ref: string): number {
  const m = ref.match(/^([A-Z]+)/i);
  if (!m) return -1;
  let n = 0;
  for (const ch of m[1].toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

// Cititor de rezervă pentru registrele XLSX pe care SheetJS nu le deschide (ex. exportul ANRE, cu prefixul „x:” pe toate
// elementele și fără referințe de celule): parcurge direct XML-ul foii, cu șirurile partajate și cele inline.
export function parseazaXlsxSimplu(
  fisiere: Record<string, Uint8Array>,
  decodeaza: (u: Uint8Array) => string,
  foaie?: string | null
): TabelParsat {
  const numeFoi = Object.keys(fisiere)
    .filter((n) => /^xl\/worksheets\/sheet\d+\.xml$/.test(n))
    .sort(
      (a, b) => Number(a.match(/(\d+)/)?.[1]) - Number(b.match(/(\d+)/)?.[1])
    );
  if (numeFoi.length === 0) throw new Error("Registrul nu conține foi");
  const workbook = fisiere["xl/workbook.xml"]
    ? decodeaza(fisiere["xl/workbook.xml"])
    : "";
  const foi = Array.from(
    workbook.matchAll(/<(?:\w+:)?sheet\b[^>]*\bname="([^"]*)"/g)
  ).map((m) => decodeazaEntitatiXml(m[1]));
  let idx = foaie ? foi.indexOf(foaie) : 0;
  if (idx < 0 || idx >= numeFoi.length) idx = 0;
  const partajate: string[] = [];
  if (fisiere["xl/sharedStrings.xml"]) {
    const bucati = decodeaza(fisiere["xl/sharedStrings.xml"]).split(
      /<\/(?:\w+:)?si>/
    );
    for (const b of bucati) {
      const inceput = b.search(/<(?:\w+:)?si\b/);
      if (inceput < 0) continue;
      partajate.push(
        Array.from(
          b
            .slice(inceput)
            .matchAll(/<(?:\w+:)?t\b[^>]*>([\s\S]*?)<\/(?:\w+:)?t>/g)
        )
          .map((t) => t[1])
          .join("")
      );
    }
  }
  const xml = decodeaza(fisiere[numeFoi[idx]]);
  const randuri: string[][] = [];
  const marcajV = /<(?:\w+:)?v>/;
  const marcajRand = /<(?:\w+:)?row\b/;
  const marcajCelula = /<(?:\w+:)?c\b/;
  for (const bucata of xml.split(/<\/(?:\w+:)?row>/)) {
    const inceput = bucata.search(marcajRand);
    if (inceput < 0) continue;
    const corpRand = bucata.slice(bucata.indexOf(">", inceput) + 1);
    const celule: string[] = [];
    let col = 0;
    const parti = corpRand.split(marcajCelula);
    for (let i = 1; i < parti.length; i++) {
      const p = parti[i];
      const finAtribute = p.indexOf(">");
      if (finAtribute < 0) continue;
      const atribute = p.slice(0, finAtribute);
      const ref = atribute.match(/\br="([A-Z]+)\d+"/i);
      if (ref) col = coloanaDinReferinta(ref[1]);
      const tip = atribute.match(/\bt="([^"]*)"/)?.[1] ?? "n";
      let valoare = "";
      if (!atribute.endsWith("/")) {
        const corp = p.slice(finAtribute + 1);
        const iv = corp.search(marcajV);
        if (iv >= 0) {
          const vs = corp.indexOf(">", iv) + 1;
          const ve = corp.indexOf("</", vs);
          const v = ve >= 0 ? corp.slice(vs, ve) : corp.slice(vs);
          valoare = tip === "s" ? (partajate[Number(v)] ?? "") : v;
        } else if (tip === "inlineStr") {
          valoare = Array.from(
            corp.matchAll(/<(?:\w+:)?t\b[^>]*>([\s\S]*?)<\/(?:\w+:)?t>/g)
          )
            .map((t) => t[1])
            .join("");
        }
      }
      celule[col] = decodeazaEntitatiXml(valoare).trim();
      col++;
    }
    for (let i = 0; i < celule.length; i++)
      if (celule[i] === undefined) celule[i] = "";
    if (celule.some((x) => x !== "")) randuri.push(celule);
  }
  if (randuri.length === 0) {
    return { antet: [], randuri: [], foaie: foi[idx], foi, mod: "tabel" };
  }
  const { antet, corp } = ridicaAntetul(randuri);
  return {
    antet,
    randuri: corp,
    foaie: foi[idx] ?? numeFoi[idx],
    foi,
    mod: "tabel",
  };
}

export function foileXlsx(buffer: ArrayBuffer): string[] {
  const wb = XLSX.read(buffer, { type: "array", bookSheets: true });
  return wb.SheetNames;
}

// registrele cu prefix de spațiu de nume pe elemente (ex. exportul ANRE) sunt refuzate de SheetJS după câteva secunde de
// procesare; se recunosc din primii octeți ai foii și merg direct la cititorul de rezervă
export function registruCuPrefix(fisiere: Record<string, Uint8Array>): boolean {
  const foaie = Object.keys(fisiere).find((n) =>
    /^xl\/worksheets\/sheet\d+\.xml$/.test(n)
  );
  if (!foaie) return false;
  const inceput = new TextDecoder("utf-8").decode(
    fisiere[foaie].subarray(0, 400)
  );
  return /<\w+:worksheet\b/.test(inceput);
}

export function parseazaXlsx(
  buffer: ArrayBuffer,
  foaie?: string | null
): TabelParsat {
  let fisiere: Record<string, Uint8Array> | null = null;
  try {
    fisiere = unzipSync(new Uint8Array(buffer));
  } catch {
    fisiere = null;
  }
  if (fisiere && registruCuPrefix(fisiere)) {
    return parseazaXlsxSimplu(fisiere, strFromU8, foaie);
  }
  try {
    return parseazaXlsxSheetJs(buffer, foaie);
  } catch (e) {
    if (!fisiere) throw e;
    return parseazaXlsxSimplu(fisiere, strFromU8, foaie);
  }
}

function parseazaXlsxSheetJs(
  buffer: ArrayBuffer,
  foaie?: string | null
): TabelParsat {
  const wb = XLSX.read(buffer, { type: "array", cellDates: true });
  const nume =
    foaie && wb.SheetNames.includes(foaie) ? foaie : wb.SheetNames[0];
  const ws = wb.Sheets[nume];
  const brute = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1,
    raw: true,
    defval: "",
  });
  const randuri = brute
    .map((r) => (Array.isArray(r) ? r.map(celulaText) : []))
    .filter((r) => r.some((v) => v !== ""));
  if (randuri.length === 0) {
    return {
      antet: [],
      randuri: [],
      foaie: nume,
      foi: wb.SheetNames,
      mod: "tabel",
    };
  }
  const { antet, corp } = ridicaAntetul(randuri);
  return {
    antet,
    randuri: corp,
    foaie: nume,
    foi: wb.SheetNames,
    mod: "tabel",
  };
}

const REGEX_CUI = /\b(?:RO\s?)?(\d{2,10})\b/g;
const REGEX_FORMA =
  /\b(S\.?R\.?L\.?|S\.?A\.?|S\.?C\.?S\.?|S\.?N\.?C\.?|P\.?F\.?A\.?|I\.?I\.?|I\.?F\.?|R\.?A\.?|SUCURSALA|FILIALA|GMBH|LTD|LLC|N\.?V\.?|S\.?E\.?)\b/i;

export function normalizeazaCui(v: unknown): string | null {
  const brut = celulaText(v);
  const cuPrefix = brut.match(/RO\s?(\d{2,10})(?![0-9])/i);
  if (cuPrefix) return cuPrefix[1];
  const curat = brut.replace(/^\s*RO/i, "").trim();
  if (/^[0-9][0-9 .]*$/.test(curat)) {
    const cifre = curat.replace(/[^0-9]/g, "");
    return cifre.length >= 2 && cifre.length <= 10 ? cifre : null;
  }
  const m = curat.match(/(?<![0-9])(\d{2,10})(?![0-9])/);
  return m ? m[1] : null;
}

export function extrageFirmeDinText(text: string): TabelParsat {
  const randuri: string[][] = [];
  const vazute = new Set<string>();
  const linii = text
    .split(/\r?\n|(?<=\.)\s{2,}/)
    .map((l) => l.replace(/\s+/g, " ").trim());
  for (let i = 0; i < linii.length; i++) {
    const linie = (linii[i] ?? "")
      .replace(/\b([A-Z]{1,2})(\d{6,10})\b/g, "$1 $2")
      .replace(
        /\b(C\.?U\.?I\.?|C\.?I\.?F\.?)\s*[:.]?\s*(?:RO\s?)?(\d{6,10})\b/gi,
        "CUI $2"
      );
    if (!linie) continue;
    const context = [linii[i - 1] ?? "", linie, linii[i + 1] ?? ""].join(" ");
    let potrivire: RegExpExecArray | null;
    const etichetat = linie.match(/\bCUI (\d{6,10})\b/);
    REGEX_CUI.lastIndex = 0;
    while ((potrivire = REGEX_CUI.exec(linie)) !== null) {
      const cui = potrivire[1];
      if (cui.length < 6 || cui.startsWith("0") || vazute.has(cui)) continue;
      if (etichetat && cui !== etichetat[1] && !vazute.has(etichetat[1]))
        continue;
      const inainte = linie.slice(0, potrivire.index);
      const numeCandidat = inainte
        .split(/[;|\t]|\s{2,}|,(?=\s*[A-ZĂÂÎȘȚ])/)
        .map((s) => s.trim())
        .filter(Boolean)
        .pop();
      const nume =
        numeCandidat && REGEX_FORMA.test(numeCandidat)
          ? numeCandidat
          : (context.match(
              new RegExp(
                `([A-ZĂÂÎȘȚ0-9][^\\n;|]{2,80}?${REGEX_FORMA.source})`,
                "i"
              )
            )?.[1] ?? "");
      vazute.add(cui);
      randuri.push([cui, nume.trim(), linie.slice(0, 200)]);
    }
  }
  if (randuri.length === 0) {
    for (const linie of linii) {
      const m = linie.match(
        new RegExp(`^(.{3,120}?${REGEX_FORMA.source})`, "i")
      );
      if (m && !vazute.has(m[1])) {
        vazute.add(m[1]);
        randuri.push(["", m[1].trim(), linie.slice(0, 200)]);
      }
    }
  }
  return { antet: ["CUI", "Denumire", "Context"], randuri, mod: "extras" };
}

export function parseazaHtml(html: string): TabelParsat {
  const doc = new DOMParser().parseFromString(html, "text/html");
  let celMaiMare: HTMLTableElement | null = null;
  let maxim = 0;
  doc.querySelectorAll("table").forEach((t) => {
    const n = t.querySelectorAll("tr").length;
    if (n > maxim) {
      maxim = n;
      celMaiMare = t;
    }
  });
  if (celMaiMare && maxim >= 3) {
    const tabel = celMaiMare as HTMLTableElement;
    const randuri: string[][] = [];
    tabel.querySelectorAll("tr").forEach((tr) => {
      const celule = Array.from(tr.querySelectorAll("th,td")).map((c) =>
        (c.textContent ?? "").replace(/\s+/g, " ").trim()
      );
      if (celule.some((v) => v !== "")) randuri.push(celule);
    });
    const { antet, corp } = ridicaAntetul(randuri);
    return { antet, randuri: corp, mod: "tabel" };
  }
  doc
    .querySelectorAll("script,style,nav,footer,header")
    .forEach((e) => e.remove());
  return extrageFirmeDinText(
    doc.body?.innerText ?? doc.body?.textContent ?? ""
  );
}

type ElementTextPdf = { str: string; transform: number[] };

export async function textDinPdf(buffer: ArrayBuffer): Promise<string> {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
  const document = await pdfjs.getDocument({ data: new Uint8Array(buffer) })
    .promise;
  const pagini: string[] = [];
  for (let p = 1; p <= document.numPages; p++) {
    const pagina = await document.getPage(p);
    const continut = await pagina.getTextContent();
    const linii = new Map<number, ElementTextPdf[]>();
    for (const element of continut.items) {
      if (!("str" in element)) continue;
      const e = element as ElementTextPdf;
      const y = Math.round(e.transform[5] / 3) * 3;
      const lista = linii.get(y) ?? [];
      lista.push(e);
      linii.set(y, lista);
    }
    const ordonate = Array.from(linii.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([, elemente]) =>
        elemente
          .sort((a, b) => a.transform[4] - b.transform[4])
          .map((e) => e.str)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim()
      );
    pagini.push(ordonate.join("\n"));
  }
  return pagini.join("\n");
}

export async function parseazaPdf(buffer: ArrayBuffer): Promise<TabelParsat> {
  return extrageFirmeDinText(await textDinPdf(buffer));
}

function textDinXmlWord(fragment: string): string {
  return fragment
    .replace(/<w:tab\/>/g, " ")
    .replace(/<w:br\/>/g, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

export function tabelDinDocxXml(xml: string): TabelParsat | null {
  const tabele = xml.match(/<w:tbl>[\s\S]*?<\/w:tbl>/g) ?? [];
  let ales: string[][] | null = null;
  for (const t of tabele) {
    const randuri = (t.match(/<w:tr[ >][\s\S]*?<\/w:tr>/g) ?? [])
      .map((tr) =>
        (tr.match(/<w:tc[ >][\s\S]*?<\/w:tc>/g) ?? []).map(textDinXmlWord)
      )
      .filter((r) => r.some((v) => v !== ""));
    if (randuri.length >= 3 && (!ales || randuri.length > ales.length))
      ales = randuri;
  }
  if (!ales) return null;
  const { antet, corp } = ridicaAntetul(ales);
  return { antet, randuri: corp, mod: "tabel" };
}

export function xmlDinDocx(buffer: ArrayBuffer): string {
  const fisiere = unzipSync(new Uint8Array(buffer));
  const document = fisiere["word/document.xml"];
  if (!document) throw new Error("Documentul Word nu a putut fi citit");
  return strFromU8(document);
}

export async function parseazaDocx(buffer: ArrayBuffer): Promise<TabelParsat> {
  const xml = xmlDinDocx(buffer);
  return tabelDinDocxXml(xml) ?? extrageFirmeDinText(await textDinDocx(buffer));
}

export async function textDinDocx(buffer: ArrayBuffer): Promise<string> {
  const fisiere = unzipSync(new Uint8Array(buffer));
  const document = fisiere["word/document.xml"];
  if (!document) throw new Error("Documentul Word nu a putut fi citit");
  const xml = strFromU8(document);
  return xml
    .replace(/<\/w:p>/g, "\n")
    .replace(/<\/w:tc>/g, "\t")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

const CHEI_CUI = [
  "cui",
  "cif",
  "cod fiscal",
  "cod unic",
  "cod de identificare",
  "codfiscal",
  "fiscal",
  "c.u.i",
  "cod inregistrare fiscala",
];
const CHEI_NUME = [
  "denumire",
  "nume",
  "societate",
  "companie",
  "firma",
  "operator",
  "entitate",
  "titular",
  "furnizor",
  "banca",
  "institutie",
  "unitate",
  "beneficiar",
  "solicitant",
  "agent economic",
  "denumirea",
];
const CHEI_REG = [
  "inmatriculare",
  "nr. reg",
  "registrul comer",
  "onrc",
  "j/",
  "nr reg",
  "nr. ord",
  "numarul de ordine",
];

const normal = (s: string) =>
  s
    .toLowerCase()
    .replace(/ă|â/g, "a")
    .replace(/î/g, "i")
    .replace(/ș|ş/g, "s")
    .replace(/ț|ţ/g, "t");

function coloanaDupaContinut(
  randuri: string[][],
  test: (v: string) => boolean,
  permise?: boolean[]
): number | null {
  const esantion = randuri.slice(0, 50);
  if (esantion.length === 0) return null;
  let ales: number | null = null;
  let maxim = 0;
  const nrColoane = Math.max(...esantion.map((r) => r.length));
  for (let c = 0; c < nrColoane; c++) {
    if (permise && permise[c] === false) continue;
    const potriviri = esantion.filter((r) => r[c] && test(r[c])).length;
    if (potriviri > maxim && potriviri >= esantion.length * 0.5) {
      maxim = potriviri;
      ales = c;
    }
  }
  return ales;
}

export function deduceColoane(
  antet: string[],
  randuri: string[][]
): MapareColoane {
  const gaseste = (chei: string[], excluse: string[] = []) => {
    // întâi antetul egal cu cheia („name”), abia apoi cel care o conține („commercialName”)
    for (const k of chei) {
      const exact = antet.findIndex((h) => normal(h).trim() === k);
      if (exact >= 0) return exact;
    }
    for (const k of chei) {
      const idx = antet.findIndex(
        (h) =>
          normal(h).includes(k) && !excluse.some((e) => normal(h).includes(e))
      );
      if (idx >= 0) return idx;
    }
    return null;
  };
  let cui = gaseste(CHEI_CUI, ["domiciliu", "sediu", "adres"]);
  let denumire = gaseste(CHEI_NUME);
  const cod = gaseste(CHEI_REG);
  if (cui === null) {
    // după conținut: numai coloane cu numere de 6-10 cifre și fără antet de licență/document/telefon (exportul ANRE are
    // numărul licenței într-o coloană numerică, iar numerele mici coincid cu CUI-uri reale)
    const antetExclus =
      /(nr|num[aă]r|licen|document|autoriz|decizi|crt|telefon|fax|data|postal|anexa|ordin)/i;
    const permise = antet.map((h) => !antetExclus.test(normal(h)));
    cui = coloanaDupaContinut(
      randuri,
      (v) => /^(RO\s?)?[1-9]\d{5,9}$/i.test(v.trim()),
      permise
    );
  }
  if (denumire === null) {
    denumire = coloanaDupaContinut(randuri, (v) => REGEX_FORMA.test(v));
  }
  return { cui, denumire, cod_inmatriculare: cod };
}

export type MapareServiciu = {
  coloana: number | null;
  valori: Record<string, string>;
};

export function valoriDistincte(
  tabel: TabelParsat,
  coloana: number | null
): string[] {
  if (coloana === null) return [];
  const set = new Set<string>();
  tabel.randuri.forEach((r) => {
    const v = (r[coloana] ?? "").trim();
    if (v) set.add(v);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function randuriPentruImport(
  tabel: TabelParsat,
  mapare: MapareColoane,
  serviciu?: MapareServiciu | null
): TargetareRandImportLista[] {
  return tabel.randuri
    .map((r) => {
      const rand: TargetareRandImportLista = {};
      tabel.antet.forEach((h, i) => {
        if (r[i] !== undefined && r[i] !== "") rand[h] = r[i];
      });
      if (serviciu && serviciu.coloana !== null) {
        const valoare = (r[serviciu.coloana] ?? "").trim();
        rand.service_id = serviciuPentruValoare(serviciu.valori, valoare);
      }
      rand.cui = mapare.cui !== null ? normalizeazaCui(r[mapare.cui]) : null;
      rand.denumire =
        mapare.denumire !== null
          ? (r[mapare.denumire] ?? "").trim() || null
          : null;
      const colJudet = tabel.antet.findIndex((h) =>
        /^(judet|județ|jud\.?|judetul|județul|judet\/sector|județ\/sector)$/i.test(
          h.trim()
        )
      );
      rand.judet = colJudet >= 0 ? (r[colJudet] ?? "").trim() || null : null;
      rand.cod_inmatriculare =
        mapare.cod_inmatriculare !== null
          ? (r[mapare.cod_inmatriculare] ?? "").trim() || null
          : null;
      return rand;
    })
    .filter((r) => r.cui || r.denumire || r.cod_inmatriculare);
}

export function csvNepotrivite(randuri: Record<string, unknown>[]): string {
  const chei = Array.from(new Set(randuri.flatMap((r) => Object.keys(r))));
  const linie = (valori: unknown[]) =>
    valori.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";");
  return [
    linie(chei),
    ...randuri.map((r) => linie(chei.map((k) => r[k]))),
  ].join("\n");
}

const normalValoare = (s: string) =>
  s
    .toLowerCase()
    .replace(/ă|â/g, "a")
    .replace(/î/g, "i")
    .replace(/ș|ş/g, "s")
    .replace(/ț|ţ/g, "t");

export function serviciuPentruValoare(
  valori: Record<string, string>,
  valoare: string
): string | null {
  const exact = valori[valoare];
  if (exact) return exact;
  const v = normalValoare(valoare);
  const chei = Object.keys(valori)
    .filter((k) => k !== "")
    .sort((a, b) => b.length - a.length);
  for (const k of chei) {
    if (v.includes(normalValoare(k))) return valori[k] || null;
  }
  return null;
}
